<script setup lang="ts">
import { computed } from 'vue';

type Align = 'left' | 'right' | 'center';

interface SimpleTableItem {
  key: string;
  label?: string;
}

const props = withDefaults(
  defineProps<{
    rows: SimpleTableItem[];
    columns: SimpleTableItem[];
    fullWidth?: boolean;
    cellClass?: string | ((cell: { row: SimpleTableItem; column: SimpleTableItem }) => string);
    rowClass?: string | ((row: SimpleTableItem) => string);
    rowHeaderClass?: string;
    align?: Align;
    rowHeaderAlign?: Align;
    /** Maakt de tabel zelf scrollbaar (beide assen) met een vastgezette kop. Vraagt een hoogte-begrenzing van de ouder. */
    stickyHeader?: boolean;
  }>(),
  {
    fullWidth: false,
    cellClass: 'px-4 py-2.5',
    rowClass: '',
    rowHeaderClass: 'bg-grey-50',
    align: 'left',
    rowHeaderAlign: undefined,
    stickyHeader: false,
  },
);

const alignClasses: Record<Align, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

const alignClass = computed(() => alignClasses[props.align]);
const rowHeaderAlignClass = computed(() => alignClasses[props.rowHeaderAlign ?? props.align]);

const resolveCellClass = (row: SimpleTableItem, column: SimpleTableItem) =>
  typeof props.cellClass === 'function' ? props.cellClass({ row, column }) : props.cellClass;

const resolveRowClass = (row: SimpleTableItem) =>
  typeof props.rowClass === 'function' ? props.rowClass(row) : props.rowClass;

defineSlots<{
  corner?: () => unknown;
  'column-header'?: (props: { column: SimpleTableItem }) => unknown;
  'row-header'?: (props: { row: SimpleTableItem }) => unknown;
  cell: (props: { row: SimpleTableItem; column: SimpleTableItem }) => unknown;
}>();
</script>

<template>
  <div
    class="rounded border border-grey-200"
    :class="[
      fullWidth ? 'w-full' : 'w-fit',
      stickyHeader ? 'max-h-full overflow-auto' : 'overflow-x-auto',
    ]"
  >
    <table class="border-collapse text-sm" :class="fullWidth ? 'w-full' : 'w-auto'">
      <thead>
        <tr class="bg-grey-50">
          <th
            class="border-r border-b border-grey-200 px-4 py-2.5 text-left"
            :class="stickyHeader && 'sticky top-0 z-10 bg-grey-50'"
          >
            <slot name="corner"></slot>
          </th>
          <th
            v-for="column in columns"
            :key="column.key"
            class="border-r border-b border-grey-200 px-4 py-2.5 font-semibold whitespace-nowrap last:border-r-0"
            :class="[alignClass, stickyHeader && 'sticky top-0 z-10 bg-grey-50']"
          >
            <slot name="column-header" :column="column">{{ column.label }}</slot>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in rows"
          :key="row.key"
          class="border-b border-grey-200 last:border-b-0"
          :class="resolveRowClass(row)"
        >
          <th
            scope="row"
            class="border-r border-grey-200 px-4 py-2.5 font-normal whitespace-nowrap"
            :class="[rowHeaderClass, rowHeaderAlignClass]"
          >
            <slot name="row-header" :row="row">{{ row.label }}</slot>
          </th>
          <td
            v-for="column in columns"
            :key="column.key"
            class="relative border-r border-grey-200 align-middle whitespace-nowrap last:border-r-0"
            :class="[resolveCellClass(row, column), alignClass]"
          >
            <slot name="cell" :row="row" :column="column"></slot>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
